import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ClipboardCheck, CheckCircle2, Shirt, Zap, Sparkles, Footprints, Package, Info, Clock, Car } from 'lucide-react';

const CategoryProcedureCard = ({ icon, title, intro, criteria, note, testid, comingSoon }) => (
  <section className="border rounded-xl shadow-sm overflow-hidden" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={testid}>
    <header className="flex items-center gap-3 px-6 md:px-8 pt-6 pb-4">
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>{title}</h2>
      </div>
      {comingSoon && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest flex-shrink-0" style={{
          background: '#111827', color: '#FFF', letterSpacing: '0.08em',
        }}>ÇOK YAKINDA</span>
      )}
    </header>

    <div className="px-6 md:px-8 pb-6 md:pb-8">
      {intro && (
        <p className="text-[15px] leading-relaxed font-medium mb-5" style={{ color: 'var(--text-secondary-strong)' }}>{intro}</p>
      )}

      {criteria && criteria.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {criteria.map((c, i) => (
            <li key={i} className="flex items-start gap-2.5 border rounded-lg p-3" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{c}</span>
            </li>
          ))}
        </ul>
      )}

      {note && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg" style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#92400E' }} />
          <p className="text-xs font-medium leading-relaxed" style={{ color: '#78350F' }}>{note}</p>
        </div>
      )}
    </div>
  </section>
);

const TestProceduresPage = () => {
  const categories = [
    {
      icon: <Shirt className="w-5 h-5" />, title: 'Giyim / Tekstil', testid: 'proc-tekstil',
      intro: 'Tekstil ürünlerinde aşağıdaki kriterler değerlendirilir:',
      criteria: [
        'Kumaşın ilk hissiyatı ve genel kalite algısı',
        'Dikiş kalitesi ve işçilik',
        'Ürün açıklaması ile fiziksel ürünün uyumu',
        'Etiket bilgileri ve içerik doğruluğu',
        'Ürünün genel görünümü',
        'Ambalaj ve sunum',
        'Fiyat-performans ilk izlenim değerlendirmesi',
      ],
    },
    {
      icon: <Sparkles className="w-5 h-5" />, title: 'Kozmetik', testid: 'proc-kozmetik',
      intro: 'Kozmetik ürünlerinde aşağıdaki kriterler değerlendirilir:',
      criteria: [
        'İçerik bilgisinin şeffaflığı',
        'Ambalaj kalitesi ve hijyen durumu',
        'Koku ve doku özellikleri',
        'Uygulama kolaylığı',
        'Ürün açıklaması ile ilk kullanım uyumu',
        'Etiket ve bilgilendirme doğruluğu',
        'Fiyat-performans ilk izlenim değerlendirmesi',
      ],
      note: 'Cilt uyumluluğu ve uzun vadeli etkiler bu test kapsamında değerlendirilmez.',
    },
    {
      icon: <Zap className="w-5 h-5" />, title: 'Elektronik', testid: 'proc-elektronik',
      intro: 'Elektronik ürünlerde aşağıdaki kriterler değerlendirilir:',
      criteria: [
        'Malzeme kalitesi ve ilk fiziksel hissiyat',
        'Temel fonksiyonların çalışıp çalışmadığı',
        'İlk kullanım performansı',
        'Kullanım kolaylığı',
        'Ürün açıklaması ile gerçek özelliklerin uyumu',
        'Kutu içeriği ve ambalaj kontrolü',
        'Fiyat-performans ilk izlenim değerlendirmesi',
      ],
    },
    {
      icon: <Footprints className="w-5 h-5" />, title: 'Ayakkabı', testid: 'proc-ayakkabi',
      intro: 'Ayakkabı ürünlerinde aşağıdaki kriterler değerlendirilir:',
      criteria: [
        'Ürün açıklaması ile fiziksel ürünün uyumu',
        'Malzeme kalitesi ve işçilik (dikiş/yapıştırma)',
        'Taban ve iç yapı dayanıklılığı (ilk izlenim)',
        'Numara doğruluğu ve etiket tutarlılığı',
        'Ambalaj ve sunum',
        'Fiyat-performans ilk izlenim değerlendirmesi',
      ],
      note: 'Uzun vadeli giyim dayanıklılığı ve yürüyüş konforu bu test kapsamında değerlendirilmez.',
    },
    {
      icon: <Package className="w-5 h-5" />, title: 'Aksesuar / Genel Ürünler', testid: 'proc-aksesuar',
      intro: 'Aksesuar ve genel ürünlerde aşağıdaki kriterler değerlendirilir:',
      criteria: [
        'Malzeme kalitesi',
        'İşçilik',
        'Temel fonksiyonların çalışması',
        'Ürün açıklaması ile uyum',
        'Ambalaj ve sunum',
        'Fiyat-performans ilk izlenim değerlendirmesi',
      ],
    },
    {
      icon: <Car className="w-5 h-5" />, title: '2. El Araç', testid: 'proc-2el-arac', comingSoon: true,
      intro: 'Bu kategori yakında aktif olacak. 2. el araç değerleme testleri; araç geçmişi, hasar kayıtları, kilometre doğrulaması ve genel mekanik/kaporta durumu değerlendirmesini içerecektir.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      <Header />

      {/* Hero */}
      <section className="px-5 md:px-6 lg:px-12 py-14 md:py-20 border-b" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="procedures-hero">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)', letterSpacing: '0.08em' }}>
            TEST PROSEDÜRLERİ
          </span>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4" style={{ color: 'var(--text-primary)' }} data-testid="procedures-title">
            Nasıl Test Ediyoruz?
          </h1>
          <p className="text-base md:text-lg font-medium leading-relaxed" style={{ color: 'var(--text-secondary-strong)' }}>
            Tüm değerlendirmelerimiz &ldquo;Hızlı Doğrulama Testi&rdquo; modeliyle yürütülür. Her ürün, kategorisine özel sabit kriterler üzerinden 15–20 dakika içinde incelenir.
          </p>
        </div>
      </section>

      {/* General Approach */}
      <section className="px-5 md:px-6 lg:px-12 py-10 md:py-12" data-testid="procedures-general">
        <div className="max-w-4xl mx-auto">
          <div className="border rounded-xl p-6 md:p-8 shadow-sm" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Genel Yaklaşım</h2>
            </div>
            <p className="text-[15px] leading-relaxed font-medium mb-4" style={{ color: 'var(--text-secondary-strong)' }}>
              TestettiK®, tüm ürün değerlendirmelerini &ldquo;Hızlı Doğrulama Testi&rdquo; modeli ile gerçekleştirir. Bu model kapsamında ürünler ortalama 15–20 dakika içerisinde:
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-4">
              {['fiziksel olarak incelenir', 'temel özellikleri kontrol edilir', 'vaatleri ile gerçek durumu karşılaştırılır'].map((t, i) => (
                <li key={i} className="flex items-start gap-2 border rounded-lg p-3" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Testler, ürünün test edildiği andaki fiziksel durumu üzerinden yapılır. Uzun süreli kullanım, dayanıklılık, performans devamlılığı veya laboratuvar testleri bu kapsamda değildir.
            </p>
          </div>
        </div>
      </section>

      {/* Category procedure cards */}
      <section className="px-5 md:px-6 lg:px-12 pb-10 md:pb-14" data-testid="procedures-category-list">
        <div className="max-w-4xl mx-auto space-y-5">
          {categories.map(cat => (
            <CategoryProcedureCard key={cat.title} {...cat} />
          ))}
        </div>
      </section>

      {/* General Test Note */}
      <section className="px-5 md:px-6 lg:px-12 pb-14 md:pb-20" data-testid="procedures-general-note">
        <div className="max-w-4xl mx-auto">
          <div className="border rounded-xl p-6 md:p-8" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Genel Test Notu</h2>
            </div>
            <p className="text-[15px] leading-relaxed font-medium mb-3" style={{ color: 'var(--text-secondary-strong)' }}>
              Tüm değerlendirmeler, ürünün test edildiği anda yapılan &ldquo;Hızlı Doğrulama Testi&rdquo; sonucudur. Bu testler:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                'uzun süreli kullanım sonuçlarını kapsamaz',
                'laboratuvar testi değildir',
                'tüm üretim partileri için garanti vermez',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary-strong)' }}>{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm font-bold leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              TestettiK® Güven Rozeti, yalnızca test edilen ürünün o anki durumunu temsil eder.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestProceduresPage;
